// Mock authentication service for development
// This file provides mock implementations of Auth0 functions for development

import { getOrgFromUrl } from '../utils/urlUtils';

// Generate mock user data with optional organization custom claim
const generateMockUser = async () => {
  const orgFromUrl = await getOrgFromUrl();
  
  const baseUser = {
    sub: 'mock|user123456',
    name: 'Development User',
    nickname: 'dev',
    picture: 'https://via.placeholder.com/150',
    email: 'dev@example.com',
    email_verified: true,
    updated_at: new Date().toISOString(),
    // Custom Auth0 claims that the application expects
    'https://prodagen.com/roles': ['ProdagenUser', 'Admin'],
    'https://prodagen.com/username': 'dev_user'
  };

  // Add organization claim if org parameter is present
  if (orgFromUrl) {
    (baseUser as any)['https://prodagen.com/org_id'] = orgFromUrl;
    console.log(`[Mock Auth] Including org_id claim: ${orgFromUrl}`);
  }

  return baseUser;
};

// Generate mock token - this is NOT a valid Auth0 token, just a placeholder
// The backend should be configured to bypass validation in development
const generateMockToken = async () => {
  const orgFromUrl = await getOrgFromUrl();
  const basePayload = {
    sub: 'mock|user123456',
    name: 'Development User',
    email: 'dev@example.com',
    iat: Math.floor(Date.now() / 1000)
  };

  // Add organization claim if org parameter is present
  if (orgFromUrl) {
    (basePayload as any)['https://prodagen.com/org_id'] = orgFromUrl;
  }

  // Create a fake JWT structure (header.payload.signature)
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(JSON.stringify(basePayload));
  return `${header}.${payload}.mock_signature`;
};

// Mock Auth0 functions
export const mockAuth0 = {
  isAuthenticated: true,
  isLoading: false,
  _cachedUser: null as any,
  
  // Initialize user asynchronously
  async initUser() {
    if (!this._cachedUser) {
      this._cachedUser = await generateMockUser();
    }
    return this._cachedUser;
  },
  
  get user() {
    // Return cached user or a basic user if not initialized yet
    return this._cachedUser || {
      sub: 'mock|user123456',
      name: 'Development User',
      email: 'dev@example.com',
      'https://prodagen.com/roles': ['ProdagenUser', 'Admin']
    };
  },
  
  // Auth methods
  loginWithRedirect: async () => {
    const orgFromUrl = await getOrgFromUrl();
    if (orgFromUrl) {
      console.log(`[Mock Auth] Organization login with redirect: ${orgFromUrl}`);
    } else {
      console.log('[Mock Auth] Login with redirect called');
    }
    return Promise.resolve();
  },
  
  logout: () => {
    console.log('[Mock Auth] Logout called');
    return Promise.resolve();
  },
  
  getAccessTokenSilently: async () => {
    console.log('[Mock Auth] Get access token silently');
    const token = await generateMockToken();
    return token;
  },
  
  getIdTokenClaims: async () => {
    console.log('[Mock Auth] Get ID token claims');
    const token = await generateMockToken();
    const user = await generateMockUser();
    return {
      __raw: token,
      ...user
    };
  },
  
  handleRedirectCallback: () => {
    console.log('[Mock Auth] Handle redirect callback');
    return Promise.resolve({ appState: {} });
  }
};

// Helper to determine if mock auth should be used
export const useMockAuth = () => {
  return process.env.REACT_APP_USE_MOCK_AUTH === 'true';
};
